/**
 * The show-once modal for a freshly created agent key.
 *
 * ===========================================================================
 * THIS DIALOG IS DELIBERATELY HARD TO CLOSE, AND THAT IS THE FEATURE
 * ===========================================================================
 * The API stores the key as a sha256 hash (`agent-keys.service.ts` header): the
 * raw key exists in exactly two places — this response, and wherever the
 * operator now puts it. There is no "show it again". A dialog that closes on a
 * stray overlay click or a reflexive Escape therefore doesn't lose a view, it
 * loses the CREDENTIAL, and the only recovery is revoke-and-reissue with a
 * truck-side agent offline in the meantime.
 *
 * So every casual dismissal path is closed on purpose:
 *   · overlay click     — `onPointerDownOutside` prevented;
 *   · Escape            — `onEscapeKeyDown` prevented;
 *   · focus wandering   — `onInteractOutside` prevented;
 *   · no X, no Cancel   — the ONLY exit is "Done", and it stays disabled until
 *     the operator has checked "I have stored this key".
 * The checkbox is deliberate friction: it converts "dismiss" into a recorded
 * claim the operator makes to themselves. This inverts the ConfirmDialog rule
 * (cancel must be easy) because here closing IS the destructive action.
 *
 * The key is rendered in a selectable mono block AND behind a copy button —
 * clipboard APIs fail in odd embeddings, and when they do the failure is said
 * out loud instead of pretending ("Copy failed — select the key and copy it
 * manually").
 */

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, Copy } from 'lucide-react';
import { Alert, Button, Checkbox } from '../../components/ui';
import type { CreatedAgentKeyDto } from './infra-api';

export function ShowOnceKeyDialog({
  created,
  onDone,
}: {
  /** `null` = closed. The parent must DROP the key from state on `onDone`. */
  created: CreatedAgentKeyDto | null;
  onDone: () => void;
}): React.JSX.Element | null {
  if (created === null) return null;
  // Keyed remount per key: the "stored" checkbox and copy state must never
  // arrive pre-ticked from a previous key (the LostReasonDialog pattern).
  return <ShowOnceKeyPrompt key={created.id} created={created} onDone={onDone} />;
}

function ShowOnceKeyPrompt({
  created,
  onDone,
}: {
  created: CreatedAgentKeyDto;
  onDone: () => void;
}): React.JSX.Element {
  const [stored, setStored] = useState(false);
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied('copied');
    } catch {
      setCopied('failed');
    }
  };

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(520px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-surface p-6 shadow-[var(--crf-shadow-overlay)]"
          // All three dismissal seams closed — see the file header for why.
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <Dialog.Title className="text-h3 text-fg">Store this key now</Dialog.Title>
          <Dialog.Description className="mt-1 text-label text-fg-secondary">
            {`This is the only time '${created.label}' is shown. Paste it into the agent's configuration before closing — it cannot be shown again, only revoked and reissued.`}
          </Dialog.Description>

          <div className="mt-4 flex items-center gap-2 rounded-sm border border-line bg-subtle p-3">
            <code
              data-testid="agent-key-once"
              className="min-w-0 flex-1 select-all break-all font-mono text-mono text-fg"
            >
              {created.key}
            </code>
            <Button variant="ghost" className="shrink-0" onClick={() => void copy()}>
              {copied === 'copied' ? (
                <>
                  <Check aria-hidden="true" className="size-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy aria-hidden="true" className="size-3.5" />
                  Copy key
                </>
              )}
            </Button>
          </div>
          {copied === 'failed' ? (
            // The Alert's tint pair (4.52:1, mode-invariant) — bare danger text
            // on the dark surface would measure 2.98:1.
            <Alert tone="danger" className="mt-2">
              Copy failed — select the key and copy it manually.
            </Alert>
          ) : null}

          <div className="mt-4 flex flex-col gap-3">
            <Checkbox
              label="I have stored this key"
              checked={stored}
              onChange={(event) => setStored(event.target.checked)}
            />
            <div className="flex justify-end">
              <Button onClick={onDone} disabled={!stored}>
                Done
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
